// The call's microphone: getUserMedia -> the framer worklet (20 ms at 16 kHz,
// public/audio/otto-mic-worklet.js) -> the gate (micGate.ts) -> the socket.
// Ported in spirit from ottos-consult src/consult/useGeminiMicStream.js
// (PORTED-FROM.md); the frame handling is a pure processor so it is tested.
import { MIC_FRAME_MS, MIC_FRAME_SAMPLES } from "@otto/protocol";
import { MicSettings } from "@otto/config";
import { audioContextCtor, takePrimedCaptureContext } from "./audioPrime";
import { createEchoGuard } from "./echoGuard";
import { createMicGateRouter, silentPcm16, type MicGateOptions } from "./micGate";
import { requestMicrophone, takeMicrophoneStream, type MicProblem } from "./micPermission";
import { meterBands, meterLevel, rms } from "./pcm";

export const MIC_WORKLET_URL = "/audio/otto-mic-worklet.js?v=1";

/**
 * After mute, silence goes out only long enough for the voice engine's end of
 * speech to close the owner's sentence (kept from today: 3 s). Past that a long
 * mute sends nothing, so muted minutes are not streamed or billed.
 */
export const MUTE_SILENCE_TAIL_MS = 3_000;

export interface MicFrameInfo {
  /** Raw RMS of the frame (the gate's and the thinking look's measure). */
  rms: number;
  /** 0..1 meter level. */
  level: number;
  bands: number[];
  /** Audio time of the frame: frame count x 20 ms. */
  audioMs: number;
}

/**
 * Pure per-frame logic: gate, pre-roll, mute tail. `send` gets each PCM16
 * buffer to put on the wire, in order.
 */
export function createMicFrameProcessor(options: {
  gate: MicGateOptions;
  send: (pcm: ArrayBuffer) => void;
  /** Otto's output level now (raw RMS), for the echo guard. Absent: no guard. */
  echoLevel?: () => number;
  /** True while Otto's audio is arriving or playing, before the player has measured it. */
  echoPending?: () => boolean;
}) {
  const router = createMicGateRouter(options.gate);
  const guard = options.echoLevel ? createEchoGuard(options.gate.echoMargin) : null;
  let frames = 0;
  let muted = false;
  let mutedSilenceMs = 0;
  const counts = { sent: 0, preroll: 0, attenuated: 0, silenced: 0, dropped: 0, mutedSilence: 0, echoGuarded: 0 };

  return {
    counts,
    get muted() {
      return muted;
    },
    setMuted(value: boolean) {
      if (value === muted) return;
      muted = value;
      mutedSilenceMs = 0;
      // Held audio from before a mute is thrown away, never sent late.
      router.reset();
    },
    frame(samples: Float32Array): MicFrameInfo {
      const audioMs = frames * MIC_FRAME_MS;
      frames += 1;
      const frameRms = rms(samples);
      if (muted) {
        if (options.gate.mode !== "drop" && mutedSilenceMs < MUTE_SILENCE_TAIL_MS) {
          options.send(silentPcm16(samples.length));
          counts.mutedSilence += 1;
          mutedSilenceMs += MIC_FRAME_MS;
        }
        return { rms: 0, level: 0, bands: meterBands(new Float32Array(0)), audioMs };
      }
      const echoThreshold = guard && options.echoLevel ? guard.threshold(frameRms, options.echoLevel(), audioMs, options.echoPending?.() ?? false) : 0;
      // A frame the normal gate would pass but the echo guard holds back (the telemetry's echo count).
      if (echoThreshold > options.gate.threshold && frameRms >= options.gate.threshold && frameRms < echoThreshold) counts.echoGuarded += 1;
      const out = router.process(samples, frameRms, audioMs, MIC_FRAME_MS, echoThreshold);
      for (const frame of out.frames) {
        counts[frame.kind] += 1;
        options.send(frame.pcm);
      }
      counts.dropped += out.dropped;
      return { rms: frameRms, level: meterLevel(samples), bands: meterBands(samples), audioMs };
    },
  };
}

export type MicCaptureState = "idle" | "starting" | "capturing" | "muted" | "suspended" | "failed";

export interface MicCaptureOptions {
  gate: MicGateOptions;
  settings?: MicSettings;
  send: (pcm: ArrayBuffer) => void;
  /** Otto's output level now (raw RMS), for the echo guard. */
  echoLevel?: () => number;
  echoPending?: () => boolean;
  onFrame: (info: MicFrameInfo) => void;
  onState: (state: MicCaptureState, problem?: MicProblem) => void;
}

export class MicCapture {
  private readonly options: MicCaptureOptions;
  private readonly processor: ReturnType<typeof createMicFrameProcessor>;
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private stopped = false;
  private moduleLoadedOn: AudioContext | null = null;
  private onDeviceChange = () => {
    // AirPods on or off, a car connecting: if our track died, take the mic again.
    if (this.stream && this.stream.getAudioTracks().some((track) => track.readyState === "ended")) void this.restart();
  };

  constructor(options: MicCaptureOptions) {
    this.options = options;
    this.processor = createMicFrameProcessor({
      gate: options.gate,
      send: options.send,
      ...(options.echoLevel ? { echoLevel: options.echoLevel } : {}),
      ...(options.echoPending ? { echoPending: options.echoPending } : {}),
    });
  }

  get counts() {
    return this.processor.counts;
  }

  async start(): Promise<{ ok: true } | { ok: false; problem: MicProblem }> {
    this.stopped = false;
    this.options.onState("starting");
    const Ctor = audioContextCtor();
    if (!Ctor || typeof AudioWorkletNode === "undefined") {
      this.options.onState("failed", "unavailable");
      return { ok: false, problem: "unavailable" };
    }
    // The check-in tap already asked; take that stream over (one prompt only).
    let stream = takeMicrophoneStream();
    if (!stream) {
      const result = await requestMicrophone(this.options.settings);
      if (!result.granted) {
        this.options.onState("failed", result.problem);
        return { ok: false, problem: result.problem };
      }
      stream = takeMicrophoneStream() ?? result.stream;
    }
    try {
      const context = this.context ?? takePrimedCaptureContext() ?? new Ctor();
      this.context = context;
      // A module is loaded once per context; a restart after a device change reuses it.
      if (this.moduleLoadedOn !== context) {
        await context.audioWorklet.addModule(MIC_WORKLET_URL);
        this.moduleLoadedOn = context;
      }
      if (context.state === "suspended") await context.resume();
      const source = context.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(context, "otto-mic-framer", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      // A silent sink keeps the graph pulling on every browser; nothing is heard.
      const sink = context.createGain();
      sink.gain.value = 0;
      node.port.onmessage = (event: MessageEvent) => {
        const data = event.data as { type?: string; samples?: Float32Array };
        if (data?.type !== "frame" || !(data.samples instanceof Float32Array)) return;
        if (data.samples.length !== MIC_FRAME_SAMPLES) return;
        this.options.onFrame(this.processor.frame(data.samples));
      };
      // Chrome can suspend a capture context when the audio device changes, and
      // Safari marks it "interrupted" for a phone call or Siri: bring it back at once.
      context.onstatechange = () => {
        if (this.context !== context || context.state === "closed") return;
        if (context.state === "running") {
          this.options.onState(this.processor.muted ? "muted" : "capturing");
          return;
        }
        this.options.onState("suspended");
        void context.resume().catch(() => undefined);
      };
      source.connect(node);
      node.connect(sink);
      sink.connect(context.destination);
      this.stream = stream;
      this.source = source;
      this.node = node;
      this.sink = sink;
      navigator.mediaDevices?.addEventListener?.("devicechange", this.onDeviceChange);
      this.options.onState(this.processor.muted ? "muted" : "capturing");
      return { ok: true };
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      this.options.onState("failed", "error");
      return { ok: false, problem: "error" };
    }
  }

  setMuted(muted: boolean) {
    this.processor.setMuted(muted);
    this.node?.port.postMessage({ type: "reset" });
    if (this.stream) this.options.onState(muted ? "muted" : "capturing");
  }

  /** After a phone lock or background tab: resume the context, or take the mic again if the track ended. */
  async resume(): Promise<boolean> {
    if (this.stopped) return false;
    const stream = this.stream;
    if (!stream || stream.getAudioTracks().some((track) => track.readyState === "ended")) return this.restart();
    if (this.context && this.context.state !== "running") await this.context.resume().catch(() => undefined);
    return this.context?.state === "running";
  }

  private async restart(): Promise<boolean> {
    this.teardownGraph();
    const result = await this.start();
    return result.ok;
  }

  private teardownGraph() {
    navigator.mediaDevices?.removeEventListener?.("devicechange", this.onDeviceChange);
    if (this.node) this.node.port.onmessage = null;
    this.node?.disconnect();
    this.source?.disconnect();
    this.sink?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.node = null;
    this.source = null;
    this.sink = null;
    this.stream = null;
  }

  stop() {
    this.stopped = true;
    this.teardownGraph();
    const context = this.context;
    this.context = null;
    if (context) {
      context.onstatechange = null;
      void context.close().catch(() => undefined);
    }
    this.options.onState("idle");
  }
}
